import { Box, useTheme } from "@mui/material";
import type { FC } from "react";
import { INK, NEUTRAL } from "@/theme/tokens";
import { type TallyRun, TallyStrip } from "@/ui/DataViz/TallyStrip/TallyStrip";
import { type LedgerTally, SLOT_REMOVALS, SlotRemoval } from "@/utils/requestOriginsLedger";
import { footerParts, type OriginCopyContext } from "./requestOriginCopy";

type Props = { tally: LedgerTally; copy: OriginCopyContext; dataTestId: string };

/**
 * Where the request stands now: every slot it ever opened as a pill — filled, otherwise closed, still open — over the
 * same arithmetic in words, "5 opened · 2 filled · 1 lowered · 2 still open", the last part set in the primary ink.
 */
export const RequestOriginFooter: FC<Props> = ({ tally, copy, dataTestId }) => {
	const theme = useTheme();
	const parts = footerParts(tally, copy);
	const closed = SLOT_REMOVALS.filter((removal) => removal !== SlotRemoval.Filled).reduce((sum, removal) => sum + tally.removed[removal], 0);
	const runs: TallyRun[] = [
		{ id: SlotRemoval.Filled, count: tally.removed[SlotRemoval.Filled], color: theme.palette.success.main },
		{ id: "closed", count: closed, color: NEUTRAL[200] },
		{ id: "open", count: tally.stillOpen, color: theme.palette.primary.main },
	];
	const leading = parts.slice(0, -1);
	const stillOpen = parts[parts.length - 1];
	return (
		<Box data-testid={dataTestId}>
			<TallyStrip runs={runs} ariaLabel={parts.join(", ")} dataTestId={`${dataTestId}-strip`} />
			<Box sx={{ mt: 0.75, fontVariantNumeric: "tabular-nums" }}>
				{leading.map((part) => (
					<span key={part}>{part} · </span>
				))}
				<Box component="span" sx={{ color: INK.PRIMARY, fontWeight: 600 }}>
					{stillOpen}
				</Box>
			</Box>
		</Box>
	);
};
